/**
 * Cloud Functions Wrappers for Email Events
 *
 * Client-side wrappers for calling Firebase Cloud Functions related to email event configurations.
 */

import { httpsCallable } from "firebase/functions";
import { functions } from "@/base";
import type { EmailEventFormData } from "./schema/email-event.types";
import type { EmailEventType } from "../../../../../types/email-event";

/**
 * Request body for creating email event
 */
export interface CreateEmailEventRequest {
  licenciada_id: string;
  event_type: string;
  subject_template: string;
  body_template: string;
  email_account_id: string;
  enabled: boolean;
}

/**
 * Response from creating email event
 */
export interface CreateEmailEventResponse {
  success: boolean;
  event_type: string;
}

/**
 * Request body for updating email event
 */
export interface UpdateEmailEventRequest {
  licenciada_id: string;
  event_type: string;
  subject_template?: string;
  body_template?: string;
  email_account_id?: string;
  enabled?: boolean;
}

/**
 * Response from updating email event
 */
export interface UpdateEmailEventResponse {
  success: boolean;
  event_type: string;
}

/**
 * Request body for deleting email event
 */
export interface DeleteEmailEventRequest {
  licenciada_id: string;
  event_type: string;
}

/**
 * Response from deleting email event
 */
export interface DeleteEmailEventResponse {
  success: boolean;
  event_type: string;
}

/**
 * Request body for listing email events
 */
export interface ListEmailEventsRequest {
  licenciada_id: string;
}

/**
 * Response from listing email events
 */
export interface ListEmailEventsResponse {
  success: boolean;
  events: Array<{
    event_type: string;
    subject_template: string;
    body_template: string;
    email_account_id: string;
    enabled: boolean;
    created_at: unknown;
    updated_at: unknown;
    created_by: string;
    updated_by: string;
  }>;
}

/**
 * Create a new email event configuration
 * @param licenciadaId - Licenciada ID
 * @param formData - Email event form data
 * @returns Promise of creation response
 * @throws {Error} If Cloud Function call fails or validation errors occur
 * @example
 * ```tsx
 * const response = await createEmailEvent("lic123", {
 *   event_type: "ideia-comentario-mention",
 *   subject_template: "Você foi mencionado",
 *   body_template: "<p>Olá {{colaborador.nome}}</p>",
 *   email_account_id: "acc123",
 *   enabled: true
 * });
 * ```
 */
export async function createEmailEvent(
  licenciadaId: string,
  formData: EmailEventFormData
): Promise<CreateEmailEventResponse> {
  try {
    const createFunction = httpsCallable<CreateEmailEventRequest, CreateEmailEventResponse>(
      functions,
      "emailEventsFunctions-createEmailEvent"
    );

    const result = await createFunction({
      licenciada_id: licenciadaId,
      event_type: formData.event_type,
      subject_template: formData.subject_template,
      body_template: formData.body_template,
      email_account_id: formData.email_account_id,
      enabled: formData.enabled
    });

    return result.data;
  } catch (error) {
    console.error("Failed to create email event:", error);

    if (error instanceof Error) {
      throw new TypeError(`Email event creation failed: ${error.message}`);
    }

    throw new TypeError("Email event creation failed with unknown error");
  }
}

/**
 * Update an existing email event configuration
 * @param licenciadaId - Licenciada ID
 * @param eventType - Event type to update
 * @param updates - Partial email event data to update
 * @returns Promise of update response
 * @throws {Error} If Cloud Function call fails or validation errors occur
 * @example
 * ```tsx
 * const response = await updateEmailEvent("lic123", "ideia-comentario-mention", {
 *   enabled: false
 * });
 * ```
 */
export async function updateEmailEvent(
  licenciadaId: string,
  eventType: EmailEventType,
  updates: Partial<Omit<EmailEventFormData, "event_type">>
): Promise<UpdateEmailEventResponse> {
  try {
    const updateFunction = httpsCallable<UpdateEmailEventRequest, UpdateEmailEventResponse>(
      functions,
      "emailEventsFunctions-updateEmailEvent"
    );

    const result = await updateFunction({
      licenciada_id: licenciadaId,
      event_type: eventType,
      ...updates
    });

    return result.data;
  } catch (error) {
    console.error("Failed to update email event:", error);

    if (error instanceof Error) {
      throw new TypeError(`Email event update failed: ${error.message}`);
    }

    throw new TypeError("Email event update failed with unknown error");
  }
}

/**
 * Delete an email event configuration
 * @param licenciadaId - Licenciada ID
 * @param eventType - Event type to delete
 * @returns Promise of deletion response
 * @throws {Error} If Cloud Function call fails
 * @example
 * ```tsx
 * const response = await deleteEmailEvent("lic123", "ideia-comentario-mention");
 * ```
 */
export async function deleteEmailEvent(
  licenciadaId: string,
  eventType: EmailEventType
): Promise<DeleteEmailEventResponse> {
  try {
    const deleteFunction = httpsCallable<DeleteEmailEventRequest, DeleteEmailEventResponse>(
      functions,
      "emailEventsFunctions-deleteEmailEvent"
    );

    const result = await deleteFunction({
      licenciada_id: licenciadaId,
      event_type: eventType
    });

    return result.data;
  } catch (error) {
    console.error("Failed to delete email event:", error);

    if (error instanceof Error) {
      throw new TypeError(`Email event deletion failed: ${error.message}`);
    }

    throw new TypeError("Email event deletion failed with unknown error");
  }
}

/**
 * List all email events for a licenciada (calls Cloud Function)
 * Note: For real-time updates, prefer using firestore.ts subscribeToEmailEvents()
 * @param licenciadaId - Licenciada ID
 * @returns Promise of list response
 * @throws {Error} If Cloud Function call fails
 * @example
 * ```tsx
 * const response = await listEmailEventsViaFunction("lic123");
 * console.log(response.events);
 * ```
 */
export async function listEmailEventsViaFunction(licenciadaId: string): Promise<ListEmailEventsResponse> {
  try {
    const listFunction = httpsCallable<ListEmailEventsRequest, ListEmailEventsResponse>(
      functions,
      "emailEventsFunctions-listEmailEvents"
    );

    const result = await listFunction({
      licenciada_id: licenciadaId
    });

    return result.data;
  } catch (error) {
    console.error("Failed to list email events:", error);

    if (error instanceof Error) {
      throw new TypeError(`Email events listing failed: ${error.message}`);
    }

    throw new TypeError("Email events listing failed with unknown error");
  }
}
